import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useDispatch, useSelector } from 'react-redux';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ConfigurationSection, SpaceBetweenRow } from "./StyleComponents";
import Colours from '@shared/ui/atoms/Colours';
import CalendarSwitch from '@shared/ui/molecules/CalendarSwitch';
import { redirectTo, selectCurrentUser, updateCurrentUser } from "@shared/store/auth.duck";
import { Switch } from '@shared/ui/atoms/Switch';
import { updateWhereaboutsRemindersRequest } from '@features/settings/services/UserService';
import { failureNotification, successNotification } from "@shared/store/notification.duck";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { UsersFavouriteOfficesAndDesksList } from "./personal-preferences-page/UsersFavouriteOfficesAndDesksList";
import { Trans, useTranslation } from "react-i18next";
import { UsersDefaultOffice } from "./personal-preferences-page/UsersDefaultOffice";
import { UsersCarParkingDetails } from "./personal-preferences-page/UsersCarParkingDetails";
import { deepLinkTo, isInTeams } from "@shared/utils/TeamsUtils";
import { teamsTabs, uiUrl } from "@shared/services/EnvironmentVariables";
import { BirthdaySection } from "./components/BirthdaySection";
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import i18next from "i18next";
import moment from "moment";
import { WithDateLocale } from "@shared/hooks/WithDateLocale";
import { useLocation } from "react-router";
import { SectionSaveButton } from './company-management-page-v2/components/SectionSaveButton';
import {
  SettingsMenuItem,
  SettingsPageLayout,
  SettingsRibbon,
  SettingsSidebarLayout,
  useSettingsPanel
} from './components/SettingsSidebarLayout';

type PreferencesPanel = 'office' | 'notifications' | 'calendar' | 'birthday' | 'language';

const MENU_ITEMS: SettingsMenuItem<PreferencesPanel>[] = [
  { view: 'office', icon: IconTypes.Office, label: 'personal-perf.office-tab' },
  { view: 'notifications', icon: IconTypes.Notification, label: 'personal-perf.notifications-tab' },
  { view: 'calendar', icon: IconTypes.Calendar, label: 'personal-perf.calendar-tab' },
  { view: 'birthday', icon: IconTypes.Profile, label: 'personal-perf.birthday-tab' },
  { view: 'language', icon: IconTypes.Flag, label: 'personal-perf.language-tab' },
];
const MENU_VIEWS = MENU_ITEMS.map(item => item.view);

const LANGUAGES = [
  { code: 'en', label: 'languages.english' },
  { code: 'fr', label: 'languages.french' },
  { code: 'de', label: 'languages.german' },
  { code: 'es', label: 'languages.spanish' },
];

export default function PersonalPreferencesPage() {
  const dispatch = useDispatch();
  const current = useSelector(selectCurrentUser);
  const [emailSubscribed, setEmailSubscribed] = useState(false);
  const [marketingEmailsEnabled, setMarketingEmailsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const {t, i18n} = useTranslation();
  const {updateLocale} = WithDateLocale();
  const location = useLocation();
  const [activePanel, setActivePanel] = useSettingsPanel(MENU_VIEWS);
  const currentLanguage = (i18n.resolvedLanguage ?? i18n.language ?? 'en').split('-')[0];

  useEffect(() => {
    setEmailSubscribed(!!current?.allowWhereaboutsReminders);
    setMarketingEmailsEnabled(!!current?.allowMarketingEmails);
  }, [current]);

  const changeLanguage = (lng: string) => {
    moment.locale(lng);
    updateLocale();
    i18next.changeLanguage(lng);
  }

  const updateWhereaboutsReminders = async (allowWhereaboutsReminders: boolean, allowMarketingEmails: boolean) => {
    setLoading(true);
    try {
      await updateWhereaboutsRemindersRequest(allowWhereaboutsReminders, allowMarketingEmails);
      dispatch(successNotification('successfully updated whereabouts reminder'));
      dispatch(updateCurrentUser(true));
      setEmailSubscribed(allowWhereaboutsReminders);
      setMarketingEmailsEnabled(allowMarketingEmails);
    } catch {
      dispatch(failureNotification('Failed to update whereabouts reminder'));
    }
    setLoading(false);
  }

  const onBackClick = () => {
    // When opened from the Whereabouts tab (/whereabouts/preferences) go back there,
    // rather than deep-linking across to the Management tab.
    if (location.pathname.startsWith('/whereabouts')) {
      dispatch(redirectTo('/whereabouts/team'));
      return;
    }
    if (isInTeams()) {
      const externalUrl = `${uiUrl}/manage`
      deepLinkTo(teamsTabs.settings, externalUrl);
    } else {
      dispatch(redirectTo('/manage'))
    }
  }

  const panelTitle = (label: string) => <HeadlineSmall style={{marginBottom: 16}}>{t(label)}</HeadlineSmall>;

  return (
    <SettingsPageLayout>
      {loading && <LoadingSpinner fullScreen={true}/>}
      <SettingsRibbon title={'settings.personal-pref'} onBack={onBackClick} />

      <SettingsSidebarLayout menuItems={MENU_ITEMS}
                             activeView={activePanel}
                             onViewChange={setActivePanel}>

        {activePanel === 'office' && <>
          {panelTitle('personal-perf.office-tab')}
          <UsersFavouriteOfficesAndDesksList />
          <UsersDefaultOffice />
          <UsersCarParkingDetails />
          <SectionSaveButton />
        </>}

        {activePanel === 'notifications' && <>
          {panelTitle('personal-perf.notifications-tab')}
          <ConfigurationSection>
            <SpaceBetweenRow>
              <BodyLarge colour={Colours.blue}>{t('settings.email-reminder')}</BodyLarge>
              <Switch value={emailSubscribed}
                      onChange={(val: boolean) => updateWhereaboutsReminders(val, marketingEmailsEnabled)} label=""/>
            </SpaceBetweenRow>
            <BodyRegular>{t('settings.email-reminder-desc')}</BodyRegular>
          </ConfigurationSection>
          <ConfigurationSection>
            <SpaceBetweenRow>
              <BodyLarge colour={Colours.blue}>{t('settings.marketing-emails')}</BodyLarge>
              <Switch value={marketingEmailsEnabled}
                      onChange={(val: boolean) => updateWhereaboutsReminders(emailSubscribed, val)} label=""/>
            </SpaceBetweenRow>
            <BodyRegular>{t('settings.marketing-emails-desc')}</BodyRegular>
          </ConfigurationSection>
          <SectionSaveButton />
        </>}

        {activePanel === 'calendar' && <>
          {panelTitle('personal-perf.calendar-tab')}
          <ConfigurationSection>
            <SpaceBetweenRow>
              <BodyLarge colour={Colours.blue}>{t('settings.calendar-link')}</BodyLarge>
              <CalendarSwitch/>
            </SpaceBetweenRow>
            <BodyRegular style={{marginBottom: 16}}><Trans i18nKey={'settings.calendar-link-desc'} /></BodyRegular>
            <FlatContentCard style={{padding: 24}}>
              <BodyRegular>{t('settings.calendar-link-tip')}</BodyRegular>
            </FlatContentCard>
          </ConfigurationSection>
          <SectionSaveButton />
        </>}

        {activePanel === 'birthday' && <>
          {panelTitle('personal-perf.birthday-tab')}
          <BirthdaySection userId={current?.id}
                           birthdayDay={current?.birthdayDay}
                           birthdayMonth={current?.birthdayMonth} />
          <SectionSaveButton />
        </>}

        {activePanel === 'language' && <>
          {panelTitle('personal-perf.language-tab')}
          <BodyRegular style={{marginBottom: 16}}>{t('settings.languages')}</BodyRegular>
          <LanguageRow>
            {LANGUAGES.map(language => (
              <ToggleableOutlineButton key={language.code}
                                       isActive={currentLanguage === language.code}
                                       text={language.label}
                                       click={() => changeLanguage(language.code)} />
            ))}
          </LanguageRow>
        </>}

      </SettingsSidebarLayout>
    </SettingsPageLayout>
  );
}

const LanguageRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 12px;
  margin-bottom: 48px;
`;
